// Marks this browser as internal (team/QA) traffic so GA4's "Internal Traffic"
// data filter can drop it. GA4 matches on the `traffic_type` event parameter,
// which firebase.ts sets as a default parameter on every event (including the
// automatic session_start / first_visit / user_engagement) when this flag is on.
//
// A device becomes internal when either:
//   - someone opens any page with ?rm_internal=1 (?rm_internal=0 clears it), or
//   - a platform admin (custom claim isAdmin, see syncAdminClaim in functions)
//     signs in on it -- see AnalyticsIdentitySync.
// The flag lives in localStorage, so it also covers signed-out marketing pages
// and the separate test accounts used for QA on that device.

export const INTERNAL_TRAFFIC_PARAM = "rm_internal";
export const INTERNAL_TRAFFIC_PARAMS = { traffic_type: "internal" } as const;

const STORAGE_KEY = "runmark.internalTraffic";

const readFlag = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeFlag = (internal: boolean) => {
  try {
    if (internal) {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked (private mode, hardened browser): the flag just won't
    // persist, which only means this visit is counted as external.
  }
};

/**
 * Applies ?rm_internal=1 / ?rm_internal=0 from the current URL. Page analytics
 * never sends query strings (see trackPageView), so the param itself is not
 * reported anywhere.
 */
export function syncInternalTrafficFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  const value = new URLSearchParams(window.location.search).get(
    INTERNAL_TRAFFIC_PARAM
  );
  if (value === "1") {
    writeFlag(true);
  } else if (value === "0") {
    writeFlag(false);
  }
}

export function isInternalTraffic(): boolean {
  return typeof window !== "undefined" && readFlag();
}

export function rememberInternalTraffic(): void {
  if (typeof window !== "undefined") {
    writeFlag(true);
  }
}
